import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useConfig } from 'providers/useConfig/ConfigContext';
import { CastEvent, CastMediaInformation } from 'types/Cast';
import { useApp } from 'providers/useApp/AppContext';
import CastHandler from './CastHandler';

const useCastService = () => {
    const [initialised, setInitialised] = useState(false);
    const { config } = useConfig();
    const { appLanguage } = useApp();

    const [castConnected, setCastConnected] = useState<boolean>(false);
    const [deviceName, setDeviceName] = useState<string>('');
    const [mediaInfo, setMediaInfo] = useState<CastMediaInformation>(null);
    const [tooManyDevices, setTooManyDevices] = useState<boolean>(false);
    const [sessionLimit, setSessionLimit] = useState<boolean>(false);
    const [streamTakeover, setStreamTakeover] = useState<boolean>(false);
    const [recastPending, setRecastPending] = useState<boolean>(false);

    const connectedChangeListener = (newState: boolean) => {
        setCastConnected(newState);
        window.chromeCastStatus = newState;
    };

    const toggleConnect = async () => CastHandler.toggleConnect();

    const disconnect = async () => CastHandler.disconnect();

    useEffect(() => {
        if (config) {
            CastHandler.init((event, data) => {
                switch (event) {
                    case CastEvent.CASTING: {
                        connectedChangeListener(data);
                        break;
                    }
                    case CastEvent.LOADED: {
                        setInitialised(data);
                        break;
                    }
                    case CastEvent.RECEIVER_NAME: {
                        setDeviceName(data);
                        break;
                    }
                    case CastEvent.MEDIA_INFO_CHANGED: {
                        setMediaInfo(data);
                        break;
                    }
                    case CastEvent.TOO_MANY_DEVICES: {
                        setTooManyDevices(true);
                        break;
                    }
                    case CastEvent.SESSION_LIMIT: {
                        setSessionLimit(true);
                        break;
                    }
                    case CastEvent.STREAM_TAKEOVER: {
                        setStreamTakeover(true);
                        break;
                    }
                    default: {
                        // do-nothing
                        break;
                    }
                }
            }).catch((e) => console.warn('Init cast handler error', e));
            CastHandler.notify();
        }
    }, [config]);

    useEffect(() => {
        if (appLanguage && castConnected) {
            CastHandler.syncUILanguage(appLanguage);
        }
    }, [appLanguage, castConnected]);

    return useMemo(
        () => ({
            initialised,
            toggleConnect,
            disconnect,
            castConnected,
            deviceName,
            mediaInfo,
            tooManyDevices,
            setTooManyDevices,
            sessionLimit,
            setSessionLimit,
            streamTakeover,
            setStreamTakeover,
            recastPending,
            setRecastPending,
        }),
        [initialised, castConnected, deviceName, mediaInfo, tooManyDevices, sessionLimit, streamTakeover, recastPending]
    );
};

export const CastContext = createContext<ReturnType<typeof useCastService>>(null);

export const CastProvider = ({ children }) => {
    const context = useCastService();
    return <CastContext.Provider value={context}>{children}</CastContext.Provider>;
};

export const useCast = () => useContext(CastContext);
